import { Box, Switch, TextField, Typography, IconButton, Button, Divider } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { toast } from "react-toastify";
import { compressImage } from "../../../../../../../utils/imageCompressor";

const glassCard = { p: 2, borderRadius: 3, bgcolor: "rgba(255,255,255,0.7)", backdropFilter: "blur(8px)", border: "1px solid rgba(229,231,235,0.6)", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" };
const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 12 } };
const accentText = (a) => ({ ...fieldSx, "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: a }, "& .MuiInputLabel-root.Mui-focused": { color: a } });

const CarouselTab = ({ skipOption, setSkipOption, errorMessage, setErrorMessage, options, setOptions, accentColor = "#f59e0b" }) => {
  const cards = (options || []).map((o) => (typeof o === "object" ? o : { title: String(o), description: "", image: "", button: { text: "Select", action: "select" } }));

  const updateCards = (next) => {
    setOptions(next.length ? next : [emptyCard()]);
  };

  const emptyCard = () => ({ title: "", description: "", image: "", button: { text: "Select", action: "select" } });

  const addCard = () => updateCards([...cards, emptyCard()]);
  const removeCard = (i) => updateCards(cards.filter((_, idx) => idx !== i));
  const changeCard = (i, patch) => {
    const u = [...cards];
    u[i] = { ...u[i], ...patch };
    updateCards(u);
  };
  const changeButton = (i, patch) => {
    const u = [...cards];
    u[i] = { ...u[i], button: { ...(u[i].button || {}), ...patch } };
    updateCards(u);
  };

  const handleImageUpload = (i) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const compressed = await compressImage(file, 400, 0.7);
        changeCard(i, { image: compressed });
      } catch {
        toast.error("Failed to compress image");
      }
    };
    input.click();
  };

  return (
    <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Skip Option</Typography>
            <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Allow visitors to skip</Typography>
          </Box>
          <Switch checked={!!skipOption} onChange={(e) => setSkipOption(e.target.checked)} size="small" />
        </Box>
      </Box>

      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Carousel Cards</Typography>
            <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Horizontal scrollable cards (image, title, description, button)</Typography>
          </Box>
          <Button
            size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={addCard}
            sx={{ textTransform: "none", fontSize: 11, fontWeight: 600, borderRadius: 2 }}
          >
            Add Card
          </Button>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {cards.map((c, i) => (
            <Box key={i} sx={{ p: 1.5, bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: `${accentColor}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: accentColor }}>{i + 1}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Card {i + 1}</Typography>
                </Box>
                <IconButton size="small" onClick={() => removeCard(i)} sx={{ color: "#ef4444", "&:hover": { bgcolor: "#fef2f2" } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>

              <Box sx={{ display: "flex", gap: 1.5, mb: 1 }}>
                <Box
                  onClick={() => handleImageUpload(i)}
                  sx={{
                    width: 80, height: 80, borderRadius: 1.5, flexShrink: 0,
                    border: `1.5px dashed ${c.image ? accentColor : "#d0d0d0"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", overflow: "hidden", bgcolor: "#fafafa", transition: "all 0.2s",
                    "&:hover": { borderColor: accentColor, transform: "scale(1.04)" },
                  }}
                >
                  {c.image ? (
                    <img src={c.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <AddPhotoAlternateIcon sx={{ color: "#bbb", fontSize: 28 }} />
                  )}
                </Box>
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.8 }}>
                  <TextField
                    fullWidth size="small" placeholder="Title"
                    value={c.title || ""}
                    onChange={(e) => changeCard(i, { title: e.target.value })}
                    sx={accentText(accentColor)}
                  />
                  <TextField
                    fullWidth size="small" placeholder="Button text (e.g. 'View Deal')"
                    value={c.button?.text || ""}
                    onChange={(e) => changeButton(i, { text: e.target.value })}
                    sx={fieldSx}
                  />
                </Box>
              </Box>

              <TextField
                fullWidth size="small" placeholder="Description (optional, max 3 lines shown)"
                multiline rows={2}
                value={c.description || ""}
                onChange={(e) => changeCard(i, { description: e.target.value })}
                sx={fieldSx}
              />
            </Box>
          ))}
        </Box>

        {cards.length === 0 && (
          <Box sx={{ p: 3, textAlign: "center", color: "#9ca3af", fontSize: 12, border: "1.5px dashed #e5e7eb", borderRadius: 2 }}>
            No cards yet. Click "Add Card" to start.
          </Box>
        )}
      </Box>

      <Box sx={glassCard}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, color: "#1f2937" }}>Error Message</Typography>
        <TextField fullWidth placeholder="Please select a card to continue" value={errorMessage || ""} size="small"
          onChange={(e) => setErrorMessage(e.target.value)}
          sx={fieldSx} />
      </Box>
    </Box>
  );
};

export default CarouselTab;
